/**
 * Ajout rapide depuis la carte produit (maquette Figma 298:12 / 298:205).
 *
 * Le "+" deplie les tailles par-dessus la photo ; le choix part directement au
 * panier via /cart/add.js, puis le tiroir panier du theme prend le relais et
 * s'ouvre tout seul.
 *
 * Remplace la campagne Beyable, qui rendait cette fonction dependante d'un
 * script tiers charge de facon intermittente.
 */
class CvQuickAdd extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.toggle = this.querySelector('.card__quick-add-toggle');
    this.panel = this.querySelector('.card__quick-add-panel');
    this.errorNode = this.querySelector('.card__quick-add-error');
    if (!this.toggle || !this.panel) return;

    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);

    this.toggle.addEventListener('click', () => this.setOpen(this.panel.hidden));
    this.panel.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-variant-id]');
      if (choice) this.addToCart(choice);
    });
  }

  disconnectedCallback() {
    this.unbindDismiss();
  }

  setOpen(open) {
    this.panel.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
    this.classList.toggle('is-open', open);

    if (open) {
      this.bindDismiss();
      this.panel.querySelector('.card__quick-add-size:not([disabled])')?.focus();
    } else {
      this.unbindDismiss();
      this.setError('');
    }
  }

  bindDismiss() {
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  unbindDismiss() {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  onDocumentPointerDown(event) {
    if (!this.contains(event.target)) this.setOpen(false);
  }

  onDocumentKeyDown(event) {
    if (event.key !== 'Escape') return;
    this.setOpen(false);
    this.toggle.focus();
  }

  setError(message) {
    if (!this.errorNode) return;
    this.errorNode.textContent = message;
    this.errorNode.hidden = !message;
  }

  async addToCart(choice) {
    if (this.loading) return;
    this.loading = true;
    choice.classList.add('is-loading');
    this.setError('');

    // Le tiroir panier du theme sait se rendre lui-meme a partir des sections
    // renvoyees par /cart/add.js : on lui demande les memes que <product-form>.
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    const body = new FormData();
    body.append('id', choice.dataset.variantId);
    body.append('quantity', 1);

    if (cart) {
      body.append('sections', cart.getSectionsToRender().map((section) => section.id));
      body.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = body;

    try {
      const response = await fetch(window.routes.cart_add_url, config);
      const data = await response.json();

      // Shopify renvoie `status` uniquement en cas de refus (rupture, quota…),
      // avec un message deja traduit : on l'affiche tel quel.
      if (data.status) {
        this.setError(data.description || data.message || this.dataset.error);
        return;
      }

      this.setOpen(false);

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cv-quick-add',
          productVariantId: choice.dataset.variantId,
          cartData: data,
        });
      }

      // renderContents ne remplace que l'interieur du tiroir : la classe
      // `is-empty`, posee au chargement quand le panier etait vide, y resterait
      // et casserait la mise en page (tiroir centre, en-tete masque, largeur
      // reduite). <product-form> fait le meme nettoyage de son cote.
      cart.classList.remove('is-empty');
      cart.renderContents(data);
    } catch (error) {
      console.error(error);
      this.setError(this.dataset.error);
    } finally {
      this.loading = false;
      choice.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cv-quick-add')) {
  customElements.define('cv-quick-add', CvQuickAdd);
}
